/**
 * Раздел «Голос клиента» (#848) — контракт `GET /api/v1/analytics/voice`.
 * Спека: docs/plans/2026-09-28-voice-of-customer-section.md
 *
 * Ответ не знает о вкладках: вкладки — это адресаты решения, а не поля схемы,
 * и какой тип сигнала читает маркетинг, а какой — продукт, решает экран.
 * Сервер отдаёт каждый срез целиком, с нулями: ноль показывается, а не
 * прячется (#699).
 */
import { z } from 'zod/v4';
import { tierFeatureRefusalSchema } from './admin';
import { lockedDemoOnlySchema, projectIdsFilterSchema } from './common';
import { orgThemeCodeSchema } from './org-themes';
import { callCategorySchema } from './records';
import { DECLINE_REASON_CODES, QUESTION_SUBJECT_CODES } from './voice-extraction';

/** Самое широкое окно — квартал плюс час перехода на летнее время, как у «Головне» и «Возражений». */
export const VOICE_MAX_WINDOW_DAYS = 93;

/**
 * Меньше стольких разговоров строка — «пока это догадка, а не
 * закономерность». Порог один на экран, выгрузку и письмо: иначе одна и та же
 * строка в одном месте догадка, в другом — вывод.
 */
export const VOICE_SMALL_SAMPLE_CALLS = 5;

/** Сколько разговоров строка раскрывает — свежие первыми, по одному на разговор. */
export const VOICE_ITEMS_PER_ROW = 20;

/**
 * Сколько разговоров раскрывает клетка разбивки отказов (#1641) — меньше,
 * чем строка: клеток по сотрудникам столько, сколько сотрудников × причин
 * (крупнейшая организация прода — 99 сотрудников за 90 дней), и полный
 * `VOICE_ITEMS_PER_ROW` на каждую раздул бы ответ. Счёт клетки — по всем её
 * разговорам; карточка говорит «показаны последние X из Y».
 */
export const VOICE_BREAKDOWN_ITEMS_PER_CELL = 5;

/**
 * Сотрудник получает свою строку в разбивке отказов (`declines.byOperator`)
 * только с этим числом разговоров продаж за окно и больше — решение владельца
 * 05.10 (#1687), то же правило, что `STREAM_AGENT_MIN_CALLS` у возражений
 * (packages/database/src/repositories/objection-stream.ts): один отказ из
 * одного разговора — не закономерность, и экран не называет на нём человека.
 * Отказы таких сотрудников не теряются — `declines.belowThresholdCalls`.
 */
export const VOICE_BREAKDOWN_OPERATOR_MIN_CALLS = 3;

/** Сколько конкурентов в списке. Шире «Головне» (10): это полный вид, а не выжимка. */
export const VOICE_COMPETITORS_LIMIT = 30;

/** Уровни отзыва о продукте в порядке показа — от самого тяжёлого к похвале. */
export const VOICE_FEEDBACK_SEVERITIES = [
  'incident',
  'major_issue',
  'minor_issue',
  'praise',
] as const;

/** Типы рыночных сигналов — закрытое поле схемы анализа. */
export const VOICE_SIGNAL_TYPES = [
  'pricing',
  'demand',
  'trend',
  'supply',
  'regulation',
] as const;

export const VOICE_COMPETITOR_SENTIMENTS = ['positive', 'neutral', 'negative'] as const;

/** Срезы блока тем сверх распределения и категорий — см. `themeCuts` запроса. */
export const VOICE_THEME_CUTS = ['outcomes', 'questions'] as const;

/**
 * С чего считается строка темы (`since`): первая активация, возврат из архива
 * или последняя правка определения. От причины зависит подпись строки: «с
 * <даты>» или «определение изменено <дата>» (спека #845 §5, §6).
 */
export const VOICE_THEME_SINCE_REASONS = [
  'activated',
  'reactivated',
  'definition_changed',
] as const;

export const voiceFeedbackSeveritySchema = z.enum(VOICE_FEEDBACK_SEVERITIES);
export const voiceSignalTypeSchema = z.enum(VOICE_SIGNAL_TYPES);
export const voiceCompetitorSentimentSchema = z.enum(VOICE_COMPETITOR_SENTIMENTS);

/**
 * Код темы в запросе фильтра (#2098). Только длина: принадлежность набору
 * проверяет маршрут ПОСЛЕ шлюза флага тем — организация с выключенными темами
 * слышит 403 `themes_disabled`, а не 400 о формате кода.
 */
export const voiceThemeFilterCodeSchema = z.string().min(1).max(64);

/**
 * Отказы фильтра темы — `{ error, code }`:
 * - `themes_disabled` 403 — флаг `enableConversationThemes` закрыт: фильтр не
 *   применяется в обход флага (экран снимает тему и перезапрашивает);
 * - `theme_filter_invalid` 400 — кода нет среди активных тем набора
 *   (архивный, чужой, неизвестный): экран сбрасывает фильтр.
 */
export const VOICE_THEME_FILTER_ERROR_CODES = [
  'themes_disabled',
  'theme_filter_invalid',
] as const;

export const voiceOfCustomerQuerySchema = z
  .object({
    periodStart: z.string().datetime({ offset: true }).optional(),
    periodEnd: z.string().datetime({ offset: true }).optional(),
    /** @deprecated legacy single-project filter — see callStatsQuerySchema's note. */
    projectId: z.string().optional(),
    projectIds: projectIdsFilterSchema,
    /** Только разговоры этой категории; нет — все категории. */
    callCategory: callCategorySchema.optional(),
    /**
     * Как у «Головне»: без значения — без демо. Конкуренты обязаны совпадать с
     * вкладкой «Головне» на одном окне (#838), а она по умолчанию демо не
     * считает.
     */
    includeDemo: z.enum(['yes', 'no']).optional(),
    /**
     * `customerQuestions` — только блоки вопросов клиентов и причин отказа
     * (#843, #844) и границы окон, без сканов конкурентов, отзывов, сигналов,
     * потребностей и без чтения глоссария: вкладке «Продажи» нужен один ключ.
     * Те же шлюзы; ответ не кешируется никогда (`no-store`): ни с блоками, ни
     * без них (флаг и открывается, и закрывается), ни при сбое. Ответ —
     * `voiceCustomerQuestionsOnlyResponseSchema`.
     * Схема не `.strict()`: edge-api, не знающий параметра, его отбрасывает и
     * отвечает полным ответом — он тоже проходит узкую схему.
     *
     * `themes` — только блок тем разговоров (#845, спека
     * 2026-10-02-conversation-themes.md §5) и границы окон: те же шлюзы плюс
     * флаг `enableConversationThemes`, ответ не кешируется никогда, как у
     * `customerQuestions`. Ответ — `voiceThemesOnlyResponseSchema`. edge-api до
     * #845 PR 6 на это значение отвечает 400 — блок тем показывает сбой, раздел
     * работает.
     */
    only: z.enum(['customerQuestions', 'themes']).optional(),
    /**
     * `none` — блоки без того, что стоит за отказами: ни разбивок (по номеру
     * звонка, сотруднику, этапу), ни разговоров строк «Почему отказываются»;
     * их SQL не идёт, `byCallNumber` и `byOperator` пусты, `byStage` нет, у
     * строк отказов только числа. Для вкладки «Сайт и контент»: отказов она не
     * рисует, а за ними — имена сотрудников, формулировки и разговоры (#1641).
     * Только вместе с `only=customerQuestions`.
     */
    breakdowns: z.enum(['none']).optional(),
    /**
     * Какой срез тем читать сверх распределения и категорий — по одному на
     * экран, который его рисует (как `breakdowns`: чего экран не рисует, то с
     * сервера не уходит):
     *  - `outcomes` — «тема × качество» и «тема × отказы» («Продажи»);
     *  - `questions` — «тема × вопросы без ответа» («Сайт и контент»).
     * Нет — только распределение и «тема × категория». Только вместе с
     * `only=themes`.
     */
    themeCuts: z.enum(VOICE_THEME_CUTS).optional(),
    /**
     * Фильтр темы (#2098, спека 2026-10-11-voice-report-layers.md §3): каждый
     * блок ответа считается только по разговорам этой темы. Код — активная
     * тема набора организации; чужой, архивный или неизвестный — 400
     * `theme_filter_invalid`, флаг `enableConversationThemes` закрыт — 403
     * `themes_disabled` (`VOICE_THEME_FILTER_ERROR_CODES`). Ответ с фильтром
     * несёт эхо `themeFilter` и не кешируется. С `only=themes` — 400: блок тем
     * фильтром не сужается. Схема не `.strict()`: edge-api до #2098 параметр
     * отбросит и ответит без эха — экран по эху и узнаёт, что фильтр не
     * применён.
     */
    themeCode: voiceThemeFilterCodeSchema.optional(),
  })
  // `breakdowns` names a narrower read of the blocks: refused without
  // `only=customerQuestions` rather than silently ignored.
  .refine(
    (query) => query.breakdowns === undefined || query.only === 'customerQuestions',
    { message: 'breakdowns requires only=customerQuestions', path: ['breakdowns'] }
  )
  .refine((query) => query.themeCuts === undefined || query.only === 'themes', {
    message: 'themeCuts requires only=themes',
    path: ['themeCuts'],
  })
  .refine((query) => query.themeCode === undefined || query.only !== 'themes', {
    message: 'themeCode does not narrow only=themes',
    path: ['themeCode'],
  });

const countSchema = z.number().int().nonnegative();

/**
 * Эхо фильтра темы (#2098): ответ с `themeCode` считан по разговорам темы.
 * Нет эха — фильтр не применён (старый edge-api), и экран не выдаёт общие
 * числа за отфильтрованные.
 * - `calls` — разговоры темы в области окна ответа, `windowCalls` — все
 *   разговоры той же области без фильтра; оба одним запросом и без потолка,
 *   как знаменатели блоков («54 из 214 разговоров периода»);
 * - `prevComparable` — база темы (`since`) не позже начала прошлого окна.
 *   Иначе часть прошлого окна темы не знала: сервер прошлое окно не считает
 *   (его числа — нули), а экран гасит все сдвиги страницы.
 */
export const voiceThemeFilterEchoSchema = z.object({
  code: z.string(),
  prevComparable: z.boolean(),
  calls: countSchema,
  windowCalls: countSchema,
});

/**
 * Разговор, до которого раскрывается строка. `text` — формулировка из анализа
 * (контекст упоминания, отзыв, сигнал, потребность): анализ строится по
 * редактированному транскрипту, нередактированного текста здесь нет.
 */
export const voiceItemSchema = z.object({
  recordId: z.string(),
  createdAt: z.string(),
  text: z.string(),
});

export const voiceCompetitorItemSchema = voiceItemSchema.extend({
  sentiment: voiceCompetitorSentimentSchema.nullable(),
});

export const voiceFeedbackItemSchema = voiceItemSchema.extend({
  /** Продукт, как его назвал клиент: свободный текст, не ключ группировки. */
  product: z.string().nullable(),
});

/**
 * Пометка строки (#847 §4, правило Р3 — `voiceMark` в `@aica/core/voc`):
 * «выросло» и «упало» — со сдвигом доли в целых процентах, «новое»,
 * «исчезло». Считает сервер — у него срок хранения и история фактов. Поля нет
 * — пометки нет: выборка мала, сдвиг незначим, период покрыт не целиком или
 * ответ дал edge-api до #2394 §9 п. 6. «Без изменений» здесь не бывает.
 */
export const voiceMarkSchema = z.union([
  z.object({
    kind: z.enum(['up', 'down']),
    share: z.number().int().min(0).max(100),
    prevShare: z.number().int().min(0).max(100),
    delta: z.number().int().min(-100).max(100),
  }),
  z.object({ kind: z.literal('new') }),
  z.object({ kind: z.literal('gone') }),
]);

/** Общее для каждой строки: «в K из N разговоров» и сдвиг к прошлому окну. */
const voiceRowCounts = {
  calls: countSchema,
  /** Разговоров с этим в прошлом окне той же длины. */
  prevCalls: countSchema,
  mark: voiceMarkSchema.optional(),
};

export const voiceCompetitorSchema = z.object({
  name: z.string(),
  /**
   * Сведённый ключ конкурента — `resolved_key` фактов, ключ его ряда
   * (`/analytics/voice/series`, #2394 §9 п. 7). Необязателен: ответ edge-api
   * до этого поля строки без ряда, а не отказ.
   */
  key: z.string().optional(),
  /** false — глоссарий есть, а этого написания в нём нет. */
  merged: z.boolean(),
  ...voiceRowCounts,
  /**
   * Разговоры по тональности. Разговор, где о конкуренте сказали и хорошо, и
   * плохо, учтён в обеих корзинах, поэтому сумма может быть больше `calls`.
   */
  sentiment: z.object({
    positive: countSchema,
    neutral: countSchema,
    negative: countSchema,
  }),
  items: z.array(voiceCompetitorItemSchema).max(VOICE_ITEMS_PER_ROW),
});

export const voiceFeedbackRowSchema = z.object({
  severity: voiceFeedbackSeveritySchema,
  ...voiceRowCounts,
  items: z.array(voiceFeedbackItemSchema).max(VOICE_ITEMS_PER_ROW),
});

export const voiceSignalRowSchema = z.object({
  type: voiceSignalTypeSchema,
  ...voiceRowCounts,
  items: z.array(voiceItemSchema).max(VOICE_ITEMS_PER_ROW),
});

export const voiceNeedsSchema = z.object({
  ...voiceRowCounts,
  /**
   * N потребностей — своё: разговоры, где анализ собирает квалификацию
   * (`needsQualification`). По умолчанию её нет у поддержки, оплаты, жалоб,
   * отмен и подключения — в общем N они разбавили бы долю.
   */
  callsInWindow: countSchema,
  prevCallsInWindow: countSchema,
  items: z.array(voiceItemSchema).max(VOICE_ITEMS_PER_ROW),
});

/**
 * «Первая минута» звонка (#843, спека 2026-10-02 §3): вопрос с `timestamp`
 * не позже стольких секунд от начала разговора. Граница, а не классификация
 * модели; вопрос без `timestamp` (цитата не заземлилась) сюда не попадает.
 */
export const VOICE_FIRST_MINUTE_SECONDS = 60;

/**
 * Номер разговора в серии (`records.call_series_id`) для «причина × номер
 * звонка» (#844): первые три — по одному, дальше — `4+`; `none` — у разговора
 * нет номера звонка серии (вне серии, чат, пропущенный или короткий звонок).
 */
export const VOICE_SERIES_CALL_BUCKETS = ['1', '2', '3', '4+', 'none'] as const;

const questionSubjectCodeSchema = z.enum(QUESTION_SUBJECT_CODES);
const declineReasonCodeSchema = z.enum(DECLINE_REASON_CODES);

/**
 * Строка предмета вопроса (#843): разговоры, где клиент спросил о нём.
 *
 * `items` — раскрытие до разговоров тем же образцом, что у остальных строк
 * раздела: свежие первыми, по одному на разговор, `text` — формулировка
 * вопроса этого предмета в разговоре. Первые три — «три последних
 * формулировки». Пустой `text` — формулировку изъяла редакция (§7): вопрос
 * был, экран пишет «формулировка скрыта».
 */
export const voiceQuestionSubjectRowSchema = z.object({
  code: questionSubjectCodeSchema,
  ...voiceRowCounts,
  /** Разговоры, где хоть один вопрос этого предмета остался без полного ответа (`answered ≠ full`). */
  unansweredCalls: countSchema,
  items: z.array(voiceItemSchema).max(VOICE_ITEMS_PER_ROW),
  /** Раскрытие `unansweredCalls`: формулировка — вопрос без полного ответа. */
  unansweredItems: z.array(voiceItemSchema).max(VOICE_ITEMS_PER_ROW),
});

/** Строка «вопросы первой минуты» по предмету — звонки и, с `includesChats`, чаты. */
export const voiceFirstMinuteRowSchema = z.object({
  code: questionSubjectCodeSchema,
  ...voiceRowCounts,
  items: z.array(voiceItemSchema).max(VOICE_ITEMS_PER_ROW),
});

/**
 * Строка причины отказа (#844): «причина — в K из N отказов». `items` — тот же
 * образец раскрытия, `text` — причина словами клиента (`wording`).
 */
export const voiceDeclineReasonRowSchema = z.object({
  code: declineReasonCodeSchema,
  ...voiceRowCounts,
  /** Разговоры, где клиент назвал причину сам (`stated = true`), а не модель её вывела. */
  statedCalls: countSchema,
  items: z.array(voiceItemSchema).max(VOICE_ITEMS_PER_ROW),
});

/**
 * Отказы одной колонки разбивки по причинам — только причины, у которых они
 * есть. `items` — раскрытие клетки до разговоров тем же образцом, что у строк
 * причин: из тех же разговоров, что `calls` (один набор, тот же потолок), не
 * больше VOICE_BREAKDOWN_ITEMS_PER_CELL, свежие первыми, по одному на
 * разговор, `text` — причина словами клиента. Есть только в ответе
 * `only=customerQuestions` (вкладка «Продажи» — единственный экран разбивок);
 * полный ответ раздела и ответ edge-api до #1641 его не несут.
 */
const voiceBreakdownItemsSchema = z
  .array(voiceItemSchema)
  .max(VOICE_BREAKDOWN_ITEMS_PER_CELL)
  .optional();

const voiceDeclineReasonCountsSchema = z.array(
  z.object({
    code: declineReasonCodeSchema,
    calls: countSchema,
    items: voiceBreakdownItemsSchema,
  })
);

/**
 * Раскрытие отказов колонки без названной причины (`calls` − сумма
 * `reasons`) — тем же образцом и при тех же условиях, что `items` причины.
 */
const voiceWithoutReasonItemsSchema = voiceBreakdownItemsSchema;

/**
 * Вопросы клиентов (#843) и причины отказа (#844) — результат шага «голос
 * клиента» (`voice_extraction_results`) по разговорам окна. Спека:
 * docs/plans/2026-10-02-customer-questions-and-decline-reasons.md §4.
 *
 * Все числа — разговоры (`COUNT(DISTINCT record)`): два вопроса одного
 * предмета в одном звонке — один разговор. Только текущий анализ записи;
 * окно, проекты, демо и категория — те же, что у остального раздела.
 * Разговор, где шаг не отработал, — «не собрано» (`coverage`) и ни в один
 * знаменатель не входит. Все предметы и причины — всегда, в порядке наборов
 * кодов, с нулями; незнакомый код (новая версия набора) пропускается.
 */
export const voiceCustomerQuestionsSchema = z.object({
  coverage: z.object({
    /** Разговоры окна с текущим анализом (предикат «проанализировано», #822). */
    analyzedCalls: countSchema,
    /** Из них — где шаг отработал: N частоты вопросов. */
    collectedCalls: countSchema,
    /** Из них — где шага нет («не собрано»): в знаменатели не входят. */
    notCollectedCalls: countSchema,
    prevAnalyzedCalls: countSchema,
    prevCollectedCalls: countSchema,
    /**
     * «Данные с <даты>» (§6, Р3): самый ранний разговор организации
     * (`records.created_at`, как окна раздела), у текущего анализа которого
     * есть результат шага, — по всей организации, а не по выбранным проектам;
     * `null` — результатов ещё нет.
     */
    dataSince: z.string().nullable(),
  }),
  /**
   * Частота вопросов по предмету и «без ответа». Блока «вопросы перед
   * решением» (`phase = pre_decision`, спека §4) нет НАМЕРЕННО: поле `phase`
   * не прошло замер выпуска (О1) —
   * docs/eval/2026-10-03-voice-extraction-measurement.md.
   */
  questions: z.object({
    /** N: разговоры окна, где шаг отработал (с вопросами или без). */
    callsInWindow: countSchema,
    prevCallsInWindow: countSchema,
    /** Все предметы, в порядке QUESTION_SUBJECT_CODES. */
    rows: z.array(voiceQuestionSubjectRowSchema),
  }),
  /**
   * Вопросы первой минуты (спека §3). Звонок: вопрос с `timestamp ≤ 60` с;
   * вопрос без `timestamp` остаётся в знаменателе, но не в числителе. Чат
   * (#1578): вопрос из сообщения раньше первого сообщения сотрудника
   * (`message_index` < `first_agent_message_index` результата шага); чат,
   * где сотрудник не писал, — любой заземлённый вопрос. Чат без границы —
   * записанный до #1578, без отдельных сообщений или с автором неизвестной
   * роли до неё — не входит ни в числитель, ни в знаменатель.
   */
  firstMinute: z.object({
    /**
     * Прежний признак «только звонки». Остаётся `'call'`, чтобы ответ
     * разбирал и UI до #1578; что в блоке и чаты, говорит `includesChats`.
     */
    channel: z.literal('call'),
    /**
     * `true` — числитель и знаменатель блока берут и чаты (#1578). Нет поля —
     * ответ edge-api до #1578 (или из его кеша): там только звонки.
     */
    includesChats: z.boolean().optional(),
    /** N: разговоры окна, где шаг отработал и первую минуту можно измерить. */
    callsInWindow: countSchema,
    prevCallsInWindow: countSchema,
    rows: z.array(voiceFirstMinuteRowSchema),
  }),
  declines: z.object({
    /** N: разговоры окна с `conversation_outcome = declined`. */
    callsInWindow: countSchema,
    prevCallsInWindow: countSchema,
    /**
     * Разговоры окна, где шаг отработал и вынес итог разговора
     * (`conversation_outcome` не пуст — только категории продаж): отличает
     * «отказов не было» от «разговоров продаж не было — отказы не измерялись».
     * Необязательно: ответ edge-api до этого поля его не несёт.
     */
    salesCallsInWindow: countSchema.optional(),
    /** Все причины, в порядке DECLINE_REASON_CODES. */
    rows: z.array(voiceDeclineReasonRowSchema),
    /**
     * Отказы по номеру звонка в серии — правило продукта (`callSeriesNumber`,
     * `isEligibleForSeries`): номер есть только у звонка серии (завершённый,
     * не короткий, не пропущенный) и считается по всей серии, а не по окну —
     * серия, начатая до окна, даёт свой настоящий номер; пропущенные и
     * короткие звонки номера не занимают. Чат, звонок без номера и разговор
     * вне серии — корзина `none`. Все корзины, в порядке
     * VOICE_SERIES_CALL_BUCKETS; `calls` — все отказы корзины, включая отказы
     * без причины.
     */
    byCallNumber: z.array(
      z.object({
        bucket: z.enum(VOICE_SERIES_CALL_BUCKETS),
        calls: countSchema,
        reasons: voiceDeclineReasonCountsSchema,
        withoutReasonItems: voiceWithoutReasonItemsSchema,
      })
    ),
    /**
     * Отказы по сотруднику — человеку по ростеру (#1927): записи под id CRM и
     * под id аккаунта — одна строка. Имя — аккаунта, иначе первое по алфавиту.
     * Разговор без сотрудника сюда не входит.
     * Строка — только у сотрудника с VOICE_BREAKDOWN_OPERATOR_MIN_CALLS
     * разговорами продаж и больше (разговоры под тем же потолком, где шаг
     * вынес итог). Больше отказов — выше.
     */
    byOperator: z.array(
      z.object({
        agentId: z.string(),
        /**
         * Установка CRM сотрудника без аккаунта Aelo (id в CRM локален для
         * портала); `null` — у аккаунта и у записи без установки (#1927).
         * Строка — человек, а не `agentId`: один несвязанный id в двух
         * порталах — две строки, различаются они этим полем. Необязательное,
         * чтобы страница пережила edge-api, откатанный до релиза без него.
         */
        installationId: z.string().nullable().optional(),
        agentName: z.string().nullable(),
        calls: countSchema,
        reasons: voiceDeclineReasonCountsSchema,
        withoutReasonItems: voiceWithoutReasonItemsSchema,
      })
    ),
    /**
     * Отказы сотрудников, у которых меньше VOICE_BREAKDOWN_OPERATOR_MIN_CALLS
     * разговоров продаж: своих строк в `byOperator` у них нет, их отказы —
     * одним числом, без имён. Из тех же разговоров под потолком. Необязательно:
     * ответ edge-api до этого поля его не несёт.
     */
    belowThresholdCalls: countSchema.optional(),
    /** Раскрытие `belowThresholdCalls` — как `items` клетки разбивки. */
    belowThresholdItems: voiceBreakdownItemsSchema,
    /**
     * Отказы без ответственного сотрудника (`records.agent_id` пуст) — из тех
     * же разговоров под потолком, что `byOperator`: строки сотрудников плюс
     * `belowThresholdCalls` плюс это число — все отказы, которые прочитала
     * разбивка. Необязательно: ответ edge-api до этого поля его не несёт.
     */
    unassignedCalls: countSchema.optional(),
    /** Раскрытие `unassignedCalls` — как `items` клетки разбивки. */
    unassignedItems: voiceBreakdownItemsSchema,
    /**
     * Отказы по этапу воронки CRM В МОМЕНТ РАЗГОВОРА (`records.crm_stage_id`,
     * `crm_stage_semantic`). Ключ есть, только когда у организации включён
     * `stageAwareScoring` (спека §4: «× этап воронки — только при
     * stageAwareScoring»). Имён этапов здесь нет — в Postgres лежат только id;
     * подпись берёт экран (`GET /crm/stage-names`). Строка одна на этап — тройку
     * (`installationId`, `entityType`, `stageKey`); строка `stageKey: null` — «без этапа» (этап
     * не снят), она есть всегда, последней; остальные — больше отказов выше.
     * `calls` — все отказы строки, включая отказы без причины.
     */
    byStage: z
      .array(
        z.object({
          /**
           * `pipelineId::stageId` — тот же ключ, что `stageKey` в карте фаз
           * (edge-api `services/stage-phase.ts`); конвейер не снят — `''`.
           */
          stageKey: z.string().nullable(),
          stageId: z.string().nullable(),
          pipelineId: z.string().nullable(),
          /** Сущность CRM разговора (`records.crm_entity_type`): сделка, лид… */
          entityType: z.string().nullable(),
          /**
           * Установка CRM записи (`records.installation_id`): id конвейеров и
           * этапов локальны для портала, и строки двух CRM (Bitrix24 и Uspacy)
           * не склеиваются; имя этапа экран берёт из списка именно этой
           * установки (`GET /crm/stage-names`, #2872). `null` — этап не снят
           * либо у записи нет установки.
           * Нет поля — сервер старее: строка имени не получает.
           */
          installationId: z.string().nullable().optional(),
          /**
           * Фаза этапа (discovery … post_sale, other) — самая свежая непустая
           * у отказов этого этапа; `null` — фаза не выведена.
           */
          semantic: z.string().nullable(),
          calls: countSchema,
          reasons: voiceDeclineReasonCountsSchema,
          withoutReasonItems: voiceWithoutReasonItemsSchema,
        })
      )
      .optional(),
  }),
  /** Текущее окно упёрлось в потолок разговоров: числители — нижняя граница. */
  truncated: z.boolean(),
  prevTruncated: z.boolean(),
});

/**
 * Разговор, до которого раскрывается строка темы. Формулировки у темы нет
 * (`text` пуст) — вместо неё категория разговора: «тема × категория» на уровне
 * одного разговора. У раскрытия «вопросы без ответа» `text` — вопрос клиента.
 */
export const voiceThemeItemSchema = voiceItemSchema.extend({
  callCategory: callCategorySchema.nullable(),
});

/** Средний балл качества по разговорам: `null` — ни у одного балла нет. */
const voiceThemeQualitySchema = z.object({
  /** Разговоры с баллом — знаменатель среднего. */
  scoredCalls: countSchema,
  /**
   * Канонический балл качества по шкале 0–100, как у KPI раздела «Качество».
   * Целый: округляет сервер, один раз, — экран показывает число как есть.
   * Границ в схеме нет, как у баллов отчётов (`reports.ts`): канонический балл
   * диапазон не обрезает (числа модели при извлечении не ограничены), и балл
   * вне него не должен ронять весь блок тем на проверке ответа.
   */
  averageScore: z.number().nullable(),
});

/**
 * Строка темы организации (#845 §5): «тема — в K из N разговоров».
 *
 * `since` — начало базы счёта строки: позднейшее из первой активации, возврата
 * из архива и правки определения. K (`calls`) — разговоры, которым шаг присвоил
 * тему с ТЕКУЩЕЙ версией определения не раньше `since`; N (`callsInWindow`) —
 * все разговоры окна, где шаг присваивал темы не раньше `since` (тема была в
 * наборе). Оба по одному предикату — K ≤ N. Прошлое окно сравнимо, только если
 * целиком лежит после `since`; иначе `prevCalls` и `prevCallsInWindow` — `null`,
 * и строка вместо стрелки пишет «с <даты>» или «определение изменено <дата>».
 */
export const voiceThemeRowSchema = z.object({
  code: orgThemeCodeSchema,
  name: z.string().min(1),
  definition: z.string(),
  definitionVersion: z.number().int().min(1),
  since: z.string(),
  sinceReason: z.enum(VOICE_THEME_SINCE_REASONS),
  /**
   * A theme of the demo set (#2008): it counts the demo conversations only and
   * is not part of the customer's set. Optional: an answer of an edge-api
   * before #2008 has no demo set.
   */
  demo: z.boolean().optional(),
  calls: countSchema,
  callsInWindow: countSchema,
  prevCalls: countSchema.nullable(),
  prevCallsInWindow: countSchema.nullable(),
  mark: voiceMarkSchema.optional(),
  /** Раскрытие строки: свежие первыми, `text` пуст. */
  items: z.array(voiceThemeItemSchema).max(VOICE_ITEMS_PER_ROW),
  /**
   * «Тема × категория звонка»: разговоры строки по категориям — только
   * категории, где они есть, больше разговоров выше. Разговор без категории
   * сюда не входит, поэтому сумма может быть меньше `calls`.
   */
  categories: z.array(z.object({ category: callCategorySchema, calls: countSchema })),
  /** «Тема × качество» — только при `themeCuts=outcomes` и праве на агрегаты качества. */
  quality: voiceThemeQualitySchema.optional(),
  /**
   * «Тема × отказы» — только при `themeCuts=outcomes` и открытом шлюзе блоков
   * вопросов и отказов: из разговоров строки — где шаг вынес итог разговора
   * (`salesCalls`, разговоры продаж) и где итог — отказ.
   */
  declines: z.object({ salesCalls: countSchema, declinedCalls: countSchema }).optional(),
  /**
   * «Тема × вопросы без ответа» — только при `themeCuts=questions` и открытом
   * шлюзе блоков вопросов: разговоры строки, где хоть один вопрос клиента
   * остался без полного ответа (`answered ≠ full`); `items` — такой вопрос.
   */
  unanswered: z
    .object({
      calls: countSchema,
      items: z.array(voiceThemeItemSchema).max(VOICE_ITEMS_PER_ROW),
    })
    .optional(),
});

/**
 * Блок тем разговоров (#845 §5) — из результата шага «голос клиента» версии 2
 * (`voice_extraction_results`), как блоки вопросов и отказов; на факты #847
 * перейдёт вместе с ними. Все числа — разговоры; только текущий анализ
 * записи; окно, проекты, демо и категория — те же, что у раздела.
 *
 * Разговор, где шаг темы не присваивал (версия 1, шага нет) или присвоить не
 * смог (`theme_status = degraded`), ни в один знаменатель не входит. Строки —
 * все активные темы набора, с нулями, больше разговоров выше; архивных нет.
 */
export const voiceThemesSchema = z.object({
  coverage: z.object({
    /** Разговоры окна с текущим анализом. */
    analyzedCalls: countSchema,
    /** Из них — где шаг присвоил темы (`theme_status = assigned`): N блока. */
    assignedCalls: countSchema,
    prevAssignedCalls: countSchema,
    /** Из них — где шаг версии 2 отработал, а темы определить не удалось. */
    degradedCalls: countSchema,
  }),
  /**
   * «Без темы»: шаг ответил, что ни одна тема набора не подходит. Доля от
   * `coverage.assignedCalls` — мера годности набора (§4), видна всегда.
   *
   * `prevCalls` — `null`, когда набор менялся в прошлом окне или после него
   * (тема создана, активирована, отправлена в архив или возвращена, правлено
   * определение): прошлая доля посчитана по другому набору, сдвига нет. Решает
   * сервер по журналу правок набора — архивных тем в `rows` нет, и по строкам
   * этого не узнать.
   */
  uncategorized: z.object({
    calls: countSchema,
    prevCalls: countSchema.nullable(),
    mark: voiceMarkSchema.optional(),
    items: z.array(voiceThemeItemSchema).max(VOICE_ITEMS_PER_ROW),
  }),
  /** Средний балл по всем разговорам с темами — с чем сравнивается балл строки. */
  quality: voiceThemeQualitySchema.optional(),
  rows: z.array(voiceThemeRowSchema),
  /** Текущее окно упёрлось в потолок разговоров: числители — нижняя граница. */
  truncated: z.boolean(),
  prevTruncated: z.boolean(),
});

export const voiceOfCustomerResponseSchema = z.object({
  periodStart: z.string(),
  demoOnly: lockedDemoOnlySchema,
  periodEnd: z.string(),
  /** Прошлое окно, как его запросили: кончается перед `periodStart`. */
  prevPeriodStart: z.string(),
  prevPeriodEnd: z.string(),
  /**
   * «N» во фразе «в K из N» для конкурентов, отзывов и сигналов: разговоры
   * окна, где анализ собрал бизнес-аналитику. Личные и внутренние разговоры
   * (`other`) её не собирают и сюда не входят — иначе «0 из N» выдавал бы
   * несобранное за измеренный ноль. У потребностей N своё (`needs`).
   */
  callsInWindow: countSchema,
  prevCallsInWindow: countSchema,
  competitors: z.object({
    // Строки текущего окна, затем названные только в прошлом (ноль и сдвиг
    // до нуля, #1379) — каждой группы не больше VOICE_COMPETITORS_LIMIT.
    rows: z.array(voiceCompetitorSchema).max(VOICE_COMPETITORS_LIMIT * 2),
    /** Доля пар (разговор, имя), чьего написания нет в глоссарии; 0 без глоссария. */
    unmergedShare: z.number().min(0).max(1),
    unmergedExamples: z.array(z.string()),
    /** Глоссарий не прочитался: имена не сведены, строки разбиты по написаниям. */
    mergeUnavailable: z.boolean(),
  }),
  /** Все четыре уровня всегда, в порядке VOICE_FEEDBACK_SEVERITIES. */
  feedback: z.array(voiceFeedbackRowSchema),
  /** Все пять типов всегда, в порядке VOICE_SIGNAL_TYPES. */
  signals: z.array(voiceSignalRowSchema),
  /** Потребности, которые назвал клиент (`needsQualification.identifiedNeeds`). */
  needs: voiceNeedsSchema,
  /** Текущее окно упёрлось в потолок сканирования: числа — нижняя граница. */
  truncated: z.boolean(),
  /** Прошлое окно упёрлось в потолок: нижняя граница только у сравнения. */
  prevTruncated: z.boolean(),
  /**
   * Откуда числа строк (#2394 §9 п. 5): `facts` — из фактов по всему окну, и
   * `truncated` говорит только о тональности конкурентов и формулировках (их
   * читает скан текущего окна с потолком); `scan` — из скана, и `truncated`
   * — о всех числах блоков (#2596).
   */
  countsFrom: z.enum(['facts', 'scan']),
  /**
   * Вопросы клиентов и причины отказа (#843, #844). Ключ есть, ТОЛЬКО когда
   * серверный шлюз `analytics.customer_questions` (`enableCustomerQuestions`)
   * открыт для организации; закрыт — ключа нет, и запросы блоков не идут.
   */
  customerQuestions: voiceCustomerQuestionsSchema.optional(),
  /**
   * Шлюз блоков открыт, но их чтение упало: `customerQuestions` нет, и это
   * сбой, а не закрытый шлюз — экран предлагает повтор. Ответ не кешируется.
   * Ключа нет — шлюз закрыт или блоки пришли.
   */
  customerQuestionsUnavailable: z.boolean().optional(),
  /**
   * Эхо фильтра темы — только у запроса с `themeCode` (#2098). У
   * `only=customerQuestions` оно есть и при закрытом шлюзе блоков вопросов:
   * строке фильтра на «Продажах» и «Сайте» нужно число разговоров темы, даже
   * когда вопросов нет.
   */
  themeFilter: voiceThemeFilterEchoSchema.optional(),
});

/**
 * `GET /analytics/voice/access` — the tier-then-flag gate alone. The code
 * tells a plan below Pro (an upgrade) from a section switched off (an admin).
 * Legacy since #1231: the screen reads the same verdict from `gates` of
 * `GET /feature-flags`; the route stays one release for tabs on the old bundle.
 */
export const voiceAccessResponseSchema = z.union([
  z.object({ allowed: z.literal(true) }),
  z.object({
    allowed: z.literal(false),
    code: tierFeatureRefusalSchema,
  }),
]);

/**
 * Ответ `GET /analytics/voice?only=customerQuestions`: границы окон и блоки
 * вопросов и отказов. Полный ответ (edge-api без параметра) тоже проходит эту
 * схему — лишние ключи отбрасываются.
 */
export const voiceCustomerQuestionsOnlyResponseSchema =
  voiceOfCustomerResponseSchema.pick({
    periodStart: true,
    periodEnd: true,
    prevPeriodStart: true,
    prevPeriodEnd: true,
    customerQuestions: true,
    customerQuestionsUnavailable: true,
    themeFilter: true,
    demoOnly: true,
  });

/**
 * Ответ `GET /analytics/voice?only=themes`: границы окон и блок тем. `themes`
 * есть, только когда флаг `enableConversationThemes` открыт организации;
 * закрыт — ключа нет, запросы не идут. `themesUnavailable` — флаг открыт, а
 * чтение упало: сбой, а не закрытый шлюз, экран предлагает повтор.
 */
export const voiceThemesOnlyResponseSchema = voiceOfCustomerResponseSchema
  .pick({
    periodStart: true,
    periodEnd: true,
    prevPeriodStart: true,
    prevPeriodEnd: true,
    demoOnly: true,
  })
  .extend({
    themes: voiceThemesSchema.optional(),
    themesUnavailable: z.boolean().optional(),
  });

export type VoiceCustomerQuestionsOnlyResponse = z.infer<
  typeof voiceCustomerQuestionsOnlyResponseSchema
>;
export type VoiceThemesOnlyResponse = z.infer<typeof voiceThemesOnlyResponseSchema>;
export type VoiceThemes = z.infer<typeof voiceThemesSchema>;
export type VoiceThemeRow = z.infer<typeof voiceThemeRowSchema>;
export type VoiceThemeItem = z.infer<typeof voiceThemeItemSchema>;
export type VoiceThemeCuts = (typeof VOICE_THEME_CUTS)[number];
export type VoiceThemeFilterEcho = z.infer<typeof voiceThemeFilterEchoSchema>;
export type VoiceThemeFilterErrorCode = (typeof VOICE_THEME_FILTER_ERROR_CODES)[number];
export type VoiceThemeSinceReason = (typeof VOICE_THEME_SINCE_REASONS)[number];
export type VoiceAccessResponse = z.infer<typeof voiceAccessResponseSchema>;
export type VoiceOfCustomerQuery = z.infer<typeof voiceOfCustomerQuerySchema>;
export type VoiceOfCustomerResponse = z.infer<typeof voiceOfCustomerResponseSchema>;
export type VoiceItem = z.infer<typeof voiceItemSchema>;
export type VoiceMark = z.infer<typeof voiceMarkSchema>;
export type VoiceCompetitorItem = z.infer<typeof voiceCompetitorItemSchema>;
export type VoiceFeedbackItem = z.infer<typeof voiceFeedbackItemSchema>;
export type VoiceCompetitor = z.infer<typeof voiceCompetitorSchema>;
export type VoiceFeedbackRow = z.infer<typeof voiceFeedbackRowSchema>;
export type VoiceSignalRow = z.infer<typeof voiceSignalRowSchema>;
export type VoiceNeeds = z.infer<typeof voiceNeedsSchema>;
export type VoiceFeedbackSeverity = z.infer<typeof voiceFeedbackSeveritySchema>;
export type VoiceSignalType = z.infer<typeof voiceSignalTypeSchema>;
export type VoiceCustomerQuestions = z.infer<typeof voiceCustomerQuestionsSchema>;
export type VoiceQuestionSubjectRow = z.infer<typeof voiceQuestionSubjectRowSchema>;
export type VoiceFirstMinuteRow = z.infer<typeof voiceFirstMinuteRowSchema>;
export type VoiceDeclineReasonRow = z.infer<typeof voiceDeclineReasonRowSchema>;
export type VoiceSeriesCallBucket = (typeof VOICE_SERIES_CALL_BUCKETS)[number];
export type VoiceCompetitorSentiment = z.infer<typeof voiceCompetitorSentimentSchema>;
